import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Plus, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useBulkImportStore } from '@/stores';
import { ROUTE } from '@/config';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { ShippingRulesTable } from '@/components/ShippingRules/ShippingRulesTable';
import { ShippingRuleBuilderModal } from '@/components/ShippingRules/ShippingRuleBuilderModal';
import { ShippingLockBanner } from '@/components/ShippingRules/ShippingLockBanner';
import { RtlIcon } from '@/components/ui/rtl-icon';
import { useGetShippingRulesMode, useListShippingRules } from '@/hooksApi/useShippingRule';
import { useFetchListProvinces } from '@/hooksApi/useRegion';
import { buildProvinceOptions } from '@/utils/provinceLookup';
import type { ShippingRule } from '@/types';

const SettingsShippingRules = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getDraft } = useBulkImportStore();
  const locale: 'en' | 'ar' = i18n.language?.startsWith('ar') ? 'ar' : 'en';

  const { data: modeData } = useGetShippingRulesMode();
  const { data: rulesData, isLoading } = useListShippingRules();
  const { data: provincesData } = useFetchListProvinces();

  // Item 1 — Lock Shipping Rules. When true the merchant is contractually
  // barred from creating/editing rules; the backend also 403s any write.
  const isLocked = modeData?.object?.lock_shipping_rules ?? false;
  const rules = rulesData?.object ?? [];
  const provinces = useMemo(
    () => buildProvinceOptions(provincesData?.object ?? [], locale),
    [provincesData, locale],
  );

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ShippingRule | undefined>(undefined);

  const openCreate = () => {
    // Defense in depth — the button is disabled when locked, but guard the
    // handler too so a stale render can never open the builder.
    if (isLocked) return;
    setEditingRule(undefined);
    setBuilderOpen(true);
  };
  const openEdit = (rule: ShippingRule) => {
    setEditingRule(rule);
    setBuilderOpen(true);
  };
  const closeBuilder = () => {
    setBuilderOpen(false);
    setEditingRule(undefined);
  };

  useEffect(() => {
    const draft = getDraft();
    if (draft) {
      const { dismiss } = toast({
        title: t('unfinished_bulk_import'),
        duration: Infinity,
        action: (
          <ToastAction
            altText={t('return_to_bulk_import')}
            onClick={() => {
              dismiss();
              navigate(`${ROUTE.IMPORT}?autoResume=true`);
            }}
            className='bg-sd-primary text-sd-primary-foreground hover:bg-sd-primary/90'
          >
            {t('return_to_bulk_import')}
          </ToastAction>
        ),
      });
      return () => {
        dismiss();
      };
    }
  }, [getDraft, navigate, t]);

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='px-4 sm:px-8 py-4 sm:py-6 space-y-4'>
        {/* Breadcrumb */}
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground'>
          <span>{t('settings')}</span>
          <RtlIcon>
            <ChevronRight className='h-3.5 w-3.5' strokeWidth={1.5} />
          </RtlIcon>
          <span className='text-sd-foreground font-medium'>{t('shipping_rules.title')}</span>
        </nav>

        {/* Item 1 — Lock Shipping Rules. One coherent lock banner replacing
            the old informational Mode-B banner: when locked, rules are
            view-only and the merchant is routed to the ticket center. */}
        {isLocked && <ShippingLockBanner />}

        {/* How it works hint banner */}
        <div className='rounded-2xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3 text-sm'>
          <Info className='h-4 w-4 mt-0.5 shrink-0 text-blue-600' />
          <div className='space-y-1.5'>
            <div className='font-semibold text-blue-900'>
              {t('shipping_rules.how_it_works_title')}
            </div>
            <p className='text-blue-800'>{t('shipping_rules.how_it_works_intro')}</p>
            <ul className='list-disc ms-5 space-y-1 text-blue-800'>
              <li>{t('shipping_rules.how_it_works_and')}</li>
              <li>{t('shipping_rules.how_it_works_or')}</li>
              <li>{t('shipping_rules.how_it_works_priority')}</li>
            </ul>
          </div>
        </div>

        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-5 py-4 border-b border-sd-border flex items-center justify-between gap-3'>
            <div>
              <h3 className='text-base font-semibold'>{t('shipping_rules.title')}</h3>
              <p className='text-xs text-sd-muted-foreground mt-0.5'>
                {t('shipping_rules.subtitle')}
              </p>
            </div>
            {/* Item 1 — Lock Shipping Rules. The create button is visually
                disabled (not hidden) when locked so the merchant sees the
                action exists but is unavailable per the contract. */}
            <Button
              onClick={openCreate}
              disabled={isLocked}
              title={isLocked ? String(t('shipping_rules.lock_banner_title')) : undefined}
              className='h-9 gap-1.5 text-xs font-semibold bg-sd-primary text-sd-primary-foreground hover:bg-sd-primary/90'
            >
              <Plus className='h-4 w-4' />
              {t('shipping_rules.create_button')}
            </Button>
          </div>
          <ShippingRulesTable
            rules={rules}
            isLoading={isLoading}
            onEdit={openEdit}
            provinces={provinces}
            locked={isLocked}
          />
        </div>
      </div>

      <ShippingRuleBuilderModal
        open={builderOpen}
        onClose={closeBuilder}
        editing={editingRule}
        provinces={provinces}
      />
    </div>
  );
};

export default SettingsShippingRules;
